import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';

import {
  BreadcrumbItem,
  UiBadgeComponent,
  UiButtonComponent,
  UiChartComponent,
  UiChartSeriesPoint,
  UiCardComponent,
  UiDialogComponent,
  UiFilterBarComponent,
  UiFilterOption,
  UiFormActionFooterComponent,
  UiFormFieldComponent,
  UiFormSectionComponent,
  UiModalComponent,
  UiPageHeaderComponent,
  UiPageTabsHeaderComponent,
  UiPipelineStage,
  UiPipelineStripComponent,
  UiSelectComponent,
  UiSelectOption,
  UiSidebarComponent,
  UiSidebarNavItem,
  UiSidebarUser,
  UiStatCardComponent,
  UiTableActionClick,
  UiTableBadgeRule,
  UiTableColumn,
  UiTableComponent,
  UiTableDetailField,
  UiTableDetailTab,
  UiTableFilterField,
  UiToggleComponent,
  UiTopbarComponent,
  UiTopbarUser,
  UiToastComponent
} from '@shared/ui';

import { ComponentsScreenService } from '../components-screen.service';

interface TableDemoRow {
  id: string;
  component: string;
  owner: string;
  status: string;
  stock: string;
  category: string;
  startsWithTable: boolean;
}

@Component({
  selector: 'app-components-screen-list',
  standalone: true,
  imports: [
    UiPageHeaderComponent,
    UiButtonComponent,
    UiCardComponent,
    UiBadgeComponent,
    UiTableComponent,
    UiDialogComponent,
    UiFormActionFooterComponent,
    UiFormFieldComponent,
    UiFormSectionComponent,
    UiSelectComponent,
    UiModalComponent,
    UiStatCardComponent,
    UiSidebarComponent,
    UiTopbarComponent,
    UiPageTabsHeaderComponent,
    UiChartComponent,
    UiPipelineStripComponent,
    UiFilterBarComponent,
    UiToggleComponent,
    UiToastComponent
  ],
  templateUrl: './components-screen-list.component.html',
  styleUrl: './components-screen-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ComponentsScreenListComponent {
  private readonly service = inject(ComponentsScreenService);

  readonly components = this.service.components;

  readonly inputDemoValue = signal('AGENCY-001');
  readonly textareaDemoValue = signal('Visit notes and operational context.');
  readonly selectDemoValue = signal('active');
  readonly selectDemoValues = signal<string[]>(['active', 'pending']);
  readonly selectedFilter = signal('all');
  readonly toggleDemoValue = signal(false);

  readonly toastVisible = signal(false);
  readonly toastTitle = signal('Component Demo');
  readonly toastMessage = signal('Toast wrapper rendered successfully.');
  readonly toastType = signal<'info' | 'success' | 'error'>('success');
  readonly deleteDialogOpen = signal(false);
  readonly deletingRowName = signal('');
  readonly deletingRowId = signal('');

  readonly tableDemoRows = signal<TableDemoRow[]>([
    { id: '1', component: 'ui-table', owner: 'Core UI', status: 'Active', stock: 'High', category: 'Data Display', startsWithTable: true },
    { id: '2', component: 'ui-select', owner: 'Core UI', status: 'Pending', stock: 'Medium', category: 'Form Controls', startsWithTable: false },
    { id: '3', component: 'ui-button', owner: 'Core UI', status: 'Active', stock: 'High', category: 'Form Controls', startsWithTable: false },
    { id: '4', component: 'ui-toast', owner: 'Core UI', status: 'Pending', stock: 'Low', category: 'Feedback', startsWithTable: false },
    { id: '5', component: 'ui-sidebar', owner: 'Core UI', status: 'Active', stock: 'High', category: 'Navigation', startsWithTable: false }
  ]);

  readonly tableDemoColumns: UiTableColumn[] = [
    { key: 'component', label: 'Component', width: '22%', sortable: true, clickable: true },
    { key: 'owner', label: 'Owner', width: '18%', sortable: true },
    { key: 'category', label: 'Category', width: '22%', sortable: true },
    { key: 'status', label: 'Status', width: '16%', type: 'status', sortable: true },
    { key: 'stock', label: 'Priority', width: '22%', type: 'stock', sortable: true }
  ];

  readonly tableDemoDetailFields: UiTableDetailField[] = [
    { key: 'component', label: 'Component' },
    { key: 'owner', label: 'Owner' },
    { key: 'category', label: 'Category' },
    { key: 'status', label: 'Status', type: 'status' },
    { key: 'stock', label: 'Priority', type: 'stock' }
  ];

  readonly tableDemoDetailTabs: UiTableDetailTab[] = [
    {
      id: 'overview',
      label: 'Overview',
      fields: this.tableDemoDetailFields
    },
    {
      id: 'ownership',
      label: 'Ownership',
      fields: [
        { key: 'owner', label: 'Owner' },
        { key: 'category', label: 'Category' },
        { key: 'status', label: 'Status', type: 'status' }
      ]
    }
  ];

  readonly tableDemoFilterFields: UiTableFilterField[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { label: 'Active', value: 'Active' },
        { label: 'Pending', value: 'Pending' }
      ]
    },
    {
      key: 'category',
      label: 'Category',
      type: 'autocomplete',
      placeholder: 'Search category',
      options: [
        { label: 'Data Display', value: 'Data Display' },
        { label: 'Form Controls', value: 'Form Controls' },
        { label: 'Feedback', value: 'Feedback' },
        { label: 'Navigation', value: 'Navigation' }
      ]
    },
    {
      key: 'owner',
      label: 'Owner',
      type: 'radio',
      options: [
        { label: 'Core UI', value: 'Core UI' },
        { label: 'Feature Team', value: 'Feature Team' }
      ]
    },
    {
      key: 'startsWithTable',
      label: 'Has Table Prefix',
      type: 'toggle',
      placeholder: 'Only show rows whose component name starts with ui-table'
    }
  ];

  readonly tableDemoBadgeRules: Record<string, UiTableBadgeRule> = {
    active: { tone: 'green' },
    pending: { tone: 'yellow' },
    high: { tone: 'green' },
    medium: { tone: 'yellow' },
    low: { tone: 'red' }
  };

  readonly filterOptions: UiFilterOption[] = [
    { label: 'All', value: 'all' },
    { label: 'High Usage', value: 'high' },
    { label: 'Medium Usage', value: 'medium' }
  ];

  readonly selectOptions: UiSelectOption[] = [
    { label: 'Active', value: 'active' },
    { label: 'Pending', value: 'pending' },
    { label: 'Archived', value: 'archived' }
  ];

  readonly chartSeries: UiChartSeriesPoint[] = [
    { label: 'Jan', value: 18 },
    { label: 'Feb', value: 20 },
    { label: 'Mar', value: 22 },
    { label: 'Apr', value: 24 }
  ];

  readonly pipelineStages: UiPipelineStage[] = [
    { id: 'identify', label: 'Identify' },
    { id: 'design', label: 'Design' },
    { id: 'review', label: 'Review' },
    { id: 'release', label: 'Release' }
  ];

  readonly sidebarDemoItems: UiSidebarNavItem[] = [
    { label: 'Dashboard', path: '/dashboard', iconPath: 'M3 3h8v8H3zM13 3h8v5h-8zM13 10h8v11h-8zM3 13h8v8H3z' },
    { label: 'Components', path: '/components-screen', iconPath: 'M3 3h8v8H3zM13 3h8v8h-8zM3 13h8v8H3zM13 13h8v8h-8z' }
  ];

  readonly sidebarDemoUser: UiSidebarUser = {
    name: 'Amina Diallo',
    role: 'Admin',
    initials: 'AD'
  };

  readonly topbarDemoBreadcrumbs: BreadcrumbItem[] = [
    { label: 'Profile', path: '/profile' },
    { label: 'Component Screen', path: null }
  ];

  readonly topbarDemoUser: UiTopbarUser = {
    name: 'Amina Diallo',
    initials: 'AD'
  };

  onFilterChange(value: string): void {
    this.selectedFilter.set(value);
  }

  onTableAction(event: UiTableActionClick): void {
    const row = event.row as TableDemoRow;

    if (event.actionId === 'delete') {
      this.deletingRowId.set(row.id);
      this.deletingRowName.set(row.component);
      this.deleteDialogOpen.set(true);
      return;
    }

    this.toastType.set('info');
    this.toastTitle.set(`Table action: ${event.actionId}`);
    this.toastMessage.set(`Action emitted for ${row.component}.`);
    this.toastVisible.set(true);
  }

  showToast(type: 'info' | 'success' | 'error'): void {
    this.toastType.set(type);
    this.toastTitle.set(type === 'error' ? 'Validation Error' : 'Wrapper Event');
    this.toastMessage.set(
      type === 'error' ? 'This is an error-state toast example.' : 'This is a toast state demo from the component screen.'
    );
    this.toastVisible.set(true);
  }

  dismissToast(): void {
    this.toastVisible.set(false);
  }

  cancelDelete(): void {
    this.deleteDialogOpen.set(false);
    this.deletingRowId.set('');
    this.deletingRowName.set('');
  }

  confirmDelete(): void {
    const targetId = this.deletingRowId();

    this.tableDemoRows.update((rows) => rows.filter((row) => row.id !== targetId));
    this.cancelDelete();
    this.toastType.set('success');
    this.toastTitle.set('Row deleted');
    this.toastMessage.set('ui-table delete flow completed after dialog confirmation.');
    this.toastVisible.set(true);
  }
}
